/**
 * useInfrastructure Hook
 *
 * Feature: 017-backup-infrastructure-setup
 * Task: T015 - Create useInfrastructure React Query hook
 * Constitution: v1.8.0 (Multi-Subscription SQL MI Edition)
 *
 * React Query hooks for infrastructure configuration management.
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { infrastructureApi } from '../services/infrastructureApi';
import {
  InfrastructureConfigDto,
  InfrastructureConfigUpdateDto,
  AciCapacityOverrideDto,
  KeyVaultReferenceDto,
  ConfigValidationResult,
  KeyVaultValidationResult,
  CollectedHealth,
  HealthHistoryResponse,
  CapacityType,
} from '../types';

// =============================================================================
// Query Keys
// =============================================================================

export const infrastructureQueryKeys = {
  all: ['infrastructure'] as const,
  config: () => [...infrastructureQueryKeys.all, 'config'] as const,
  health: () => [...infrastructureQueryKeys.all, 'health'] as const,
  healthHistory: (params?: { component?: string; since?: string; limit?: number }) =>
    [...infrastructureQueryKeys.health(), 'history', params] as const,
  overrides: () => [...infrastructureQueryKeys.all, 'overrides'] as const,
  keyVaultReferences: () => [...infrastructureQueryKeys.all, 'keyvault', 'references'] as const,
};

// =============================================================================
// Configuration Hooks
// =============================================================================

/**
 * Hook to fetch infrastructure configuration
 */
export function useInfrastructureConfig(
  options?: Omit<UseQueryOptions<InfrastructureConfigDto | null, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<InfrastructureConfigDto | null, Error>({
    queryKey: infrastructureQueryKeys.config(),
    queryFn: infrastructureApi.getConfig,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

/**
 * Hook to update infrastructure configuration
 */
export function useUpdateInfrastructureConfig() {
  const queryClient = useQueryClient();

  return useMutation<InfrastructureConfigDto, Error, InfrastructureConfigUpdateDto>({
    mutationFn: infrastructureApi.updateConfig,
    onSuccess: (data) => {
      // Update the cache with new data
      queryClient.setQueryData(infrastructureQueryKeys.config(), data);
      // Invalidate health as config changes may affect it
      queryClient.invalidateQueries({ queryKey: infrastructureQueryKeys.health() });
    },
  });
}

/**
 * Hook to validate configuration without saving
 */
export function useValidateConfig() {
  return useMutation<ConfigValidationResult, Error, InfrastructureConfigUpdateDto>({
    mutationFn: infrastructureApi.validateConfig,
  });
}

// =============================================================================
// Health Hooks
// =============================================================================

/**
 * Hook to fetch the collected health status (Spec 216 T024/T025 — DB-only;
 * the useTriggerHealthCheck live-probe mutation is DELETED per FR-018).
 */
export function useInfrastructureHealth(
  enablePolling = false,
  pollingInterval = 60000, // 1 minute
  options?: Omit<UseQueryOptions<CollectedHealth, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<CollectedHealth, Error>({
    queryKey: infrastructureQueryKeys.health(),
    queryFn: infrastructureApi.getHealth,
    staleTime: enablePolling ? pollingInterval : 5 * 60 * 1000,
    refetchInterval: enablePolling ? pollingInterval : false,
    ...options,
  });
}

/**
 * Hook to fetch health history
 */
export function useHealthHistory(
  params?: { component?: string; since?: string; limit?: number },
  options?: Omit<UseQueryOptions<HealthHistoryResponse, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<HealthHistoryResponse, Error>({
    queryKey: infrastructureQueryKeys.healthHistory(params),
    queryFn: () => infrastructureApi.getHealthHistory(params),
    staleTime: 30 * 1000, // 30 seconds
    ...options,
  });
}

// =============================================================================
// Capacity Override Hooks
// =============================================================================

/**
 * Hook to fetch all capacity type overrides
 */
export function useCapacityOverrides(
  options?: Omit<
    UseQueryOptions<{ items: AciCapacityOverrideDto[] }, Error>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<{ items: AciCapacityOverrideDto[] }, Error>({
    queryKey: infrastructureQueryKeys.overrides(),
    queryFn: infrastructureApi.getOverrides,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Hook to create or update a capacity override
 */
export function useUpsertCapacityOverride() {
  const queryClient = useQueryClient();

  return useMutation<
    AciCapacityOverrideDto,
    Error,
    { capacityType: CapacityType; override: Omit<AciCapacityOverrideDto, 'id' | 'capacityType'> }
  >({
    mutationFn: ({ capacityType, override }) =>
      infrastructureApi.upsertOverride(capacityType, override),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: infrastructureQueryKeys.overrides() });
      queryClient.invalidateQueries({ queryKey: infrastructureQueryKeys.config() });
    },
  });
}

/**
 * Hook to delete a capacity override
 */
export function useDeleteCapacityOverride() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, CapacityType>({
    mutationFn: infrastructureApi.deleteOverride,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: infrastructureQueryKeys.overrides() });
      queryClient.invalidateQueries({ queryKey: infrastructureQueryKeys.config() });
    },
  });
}

// =============================================================================
// Key Vault Reference Hooks
// =============================================================================

/**
 * Hook to fetch all Key Vault references
 */
export function useKeyVaultReferences(
  options?: Omit<UseQueryOptions<{ items: KeyVaultReferenceDto[] }, Error>, 'queryKey' | 'queryFn'>
) {
  return useQuery<{ items: KeyVaultReferenceDto[] }, Error>({
    queryKey: infrastructureQueryKeys.keyVaultReferences(),
    queryFn: infrastructureApi.getKeyVaultReferences,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Hook to create or update a Key Vault reference
 */
export function useUpsertKeyVaultReference() {
  const queryClient = useQueryClient();

  return useMutation<
    KeyVaultReferenceDto,
    Error,
    {
      purpose: string;
      reference: Omit<
        KeyVaultReferenceDto,
        'id' | 'purpose' | 'validationStatus' | 'lastValidated'
      >;
    }
  >({
    mutationFn: ({ purpose, reference }) =>
      infrastructureApi.upsertKeyVaultReference(purpose, reference),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: infrastructureQueryKeys.keyVaultReferences() });
      queryClient.invalidateQueries({ queryKey: infrastructureQueryKeys.config() });
    },
  });
}

/**
 * Hook to delete a Key Vault reference
 */
export function useDeleteKeyVaultReference() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: infrastructureApi.deleteKeyVaultReference,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: infrastructureQueryKeys.keyVaultReferences() });
      queryClient.invalidateQueries({ queryKey: infrastructureQueryKeys.config() });
    },
  });
}

/**
 * Hook to validate all Key Vault references
 */
export function useValidateKeyVaultReferences() {
  const queryClient = useQueryClient();

  return useMutation<KeyVaultValidationResult, Error, void>({
    mutationFn: () => infrastructureApi.validateKeyVaultReferences(),
    onSuccess: () => {
      // Refresh references to get updated validation status
      queryClient.invalidateQueries({ queryKey: infrastructureQueryKeys.keyVaultReferences() });
    },
  });
}

// =============================================================================
// Exports
// =============================================================================

export default {
  // Query keys
  queryKeys: infrastructureQueryKeys,

  // Config hooks
  useInfrastructureConfig,
  useUpdateInfrastructureConfig,
  useValidateConfig,

  // Health hooks
  useInfrastructureHealth,
  useHealthHistory,

  // Override hooks
  useCapacityOverrides,
  useUpsertCapacityOverride,
  useDeleteCapacityOverride,

  // Key Vault hooks
  useKeyVaultReferences,
  useUpsertKeyVaultReference,
  useDeleteKeyVaultReference,
  useValidateKeyVaultReferences,
};
